import React, { ReactElement } from 'react';
import Link from 'next/link';
import classNames from 'classnames/bind';
import styles from 'sections/Resources/DemurrageAndDetention/components/SectionCharges/SectionCharges.module.scss';

const cn = classNames.bind(styles);

const SectionCharges = (): ReactElement => {
  return (
    <section className={cn('charges')}>
      <div className={cn('container')}>

        <h2 className={cn('charges__heading')}>What are Demurrage and Detention Charges?</h2>

        <p className={cn('charges__description')}>
          <Link className={cn('charges__link')} href="/freight-dictionary/detention-and-demurrage">Demurrage and Detention</Link> are
          fees charged by shipping lines or carriers for the use of their
          containers beyond the agreed-upon free time. Think of it as a late fee for exceeding the
          container’s allowed time at a terminal or with the consignee. There are important differences
          between demurrage and detention charges: demurrage applies when a full container stays too
          long in the terminal area, while detention applies when the consignee holds the container
          outside the terminal beyond the free time. This encourages the timely return of containers to
          the port to maintain a smooth flow of global trade.
        </p>

        <div className={cn('charges__info')}>
          <div className={cn('charges__info-container')}>
            <h3 className={cn('charges__info-heading')}>Demurrage</h3>
            <p className={cn('charges__info-paragraph')}>
              <Link className={cn('charges__link')} href="/freight-dictionary/demurrage">Demurrage</Link> refers
              specifically to the charges that accrue when you leave a container at the
              terminal (port, rail ramp) beyond the allotted free time. A demurrage charge is levied when a
              full container remains in the terminal area after the container arrives and is not unloaded or
              collected within the free time.
            </p>
            <p className={cn('charges__info-paragraph')}>
              When cargo remains on the ship or in the container beyond the allotted free time, usually
              specified in the shipping contract, the shipper, consignee, or freight forwarder must pay demurrage fees.
              These fees are paid by the responsible party, and the shipping line levied
              these charges. These fees are designed to compensate the shipping line for the additional
              costs incurred due to the delay, including the opportunity cost of not being able to use the
              vessel or container for another shipment.
            </p>
          </div>

          <div className={cn('charges__info-container')}>
            <h3 className={cn('charges__info-heading')}>Detention</h3>
            <p className={cn('charges__info-paragraph')}>
              <Link className={cn('charges__link')} href="/freight-dictionary/detention">Detention</Link> charges
              come into play when you hold onto a container outside of the terminal (e.g.,
              at your warehouse) past the free time. Detention refers to charges for containers held outside
              the terminal area after the free time, and a detention charge is incurred when the consignee
              holds the container beyond the allowed period. Essentially, it penalises delays in returning the
              empty container back to the shipping line. This often happens due to unforeseen
              circumstances like warehouse backlogs, transportation issues, or customs delays.
            </p>
            <p className={cn('charges__info-paragraph')}>
              When containers are used for storage or transport beyond the stipulated free period, the
              consignee, shipper, or freight forwarder must pay detention fees. The party who pays these
              charges may vary depending on shipping terms, and they are paid to the shipping line. These
              charges compensate the shipping line for the loss of use of their container, ensuring that the
              containers remain available for other shipments.
            </p>
          </div>
        </div>

        <p className={cn('charges__description')}>
          <span>Note:</span> Detention and demurrage can have different meanings in various logistics contexts, such
          as trucking or equipment holding.
        </p>

      </div>
    </section>
  );
};

export default SectionCharges;